import { Drawer } from "antd";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { fetchLocationContainers } from "../../services/locationServices";
import { Loader } from "../../components/Loader";

const T = "list_locations.containers_panel";

const qty = (count, one, other) => `${count} ${count === 1 ? one : other}`;

const contentSignature = (products) =>
    (products || [])
        .map((p) => `${p.product_id}:${p.pieces}:${p.lot_code || ""}`)
        .sort()
        .join("|");

// Barcodes repeat across physically identical containers, so we collapse those
// with the same barcode and content into a single "N cajas/tarimas con código X" row.
const groupBoxes = (boxes) => {
    const map = new Map();
    (boxes || []).forEach((b) => {
        const key = `${b.barcode || ""}#${contentSignature(b.products)}`;
        const group = map.get(key);
        if (group) group.count += 1;
        else map.set(key, { key, barcode: b.barcode, count: 1, products: b.products || [] });
    });
    return [...map.values()];
};

const palletSignature = (pallet) => {
    const boxes = groupBoxes(pallet.children)
        .map((g) => `${g.barcode || ""}:${g.count}:${contentSignature(g.products)}`)
        .sort()
        .join(";");
    return `${pallet.barcode || ""}#${contentSignature(pallet.products)}#${boxes}`;
};

const groupPallets = (pallets) => {
    const map = new Map();
    (pallets || []).forEach((p) => {
        const key = palletSignature(p);
        const group = map.get(key);
        if (group) group.count += 1;
        else map.set(key, { key, count: 1, pallet: p });
    });
    return [...map.values()];
};

const Badge = ({ className, children }) => (
    <span className={`text-[11px] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${className}`}>
        {children}
    </span>
);

const Barcode = ({ value, t }) =>
    value ? (
        <span className="font-mono text-sm text-gray-800">{value}</span>
    ) : (
        <span className="font-mono text-sm text-gray-400 italic">{t(`${T}.no_barcode`)}</span>
    );

const ProductLines = ({ products, highlightProductId, t }) => {
    if (!products || products.length === 0) return null;
    return (
        <ul className="mt-1.5 space-y-1">
            {products.map((p, i) => {
                const highlighted = p.product_id === highlightProductId;
                return (
                    <li
                        key={`${p.product_id}-${i}`}
                        className={`text-sm rounded-md px-2 py-1.5 ${highlighted ? "bg-indigo-50 ring-1 ring-indigo-100" : "bg-white"}`}
                    >
                        <div className="flex items-baseline justify-between gap-2">
                            <span className={`font-medium ${highlighted ? "text-indigo-800" : "text-gray-800"}`}>
                                {p.product_name}
                            </span>
                            <span className="text-gray-900 font-semibold tabular-nums whitespace-nowrap">
                                {qty(p.pieces, t(`${T}.piece_one`), t(`${T}.piece_other`))}
                            </span>
                        </div>
                        {(p.skus?.length > 0 || p.upcs?.length > 0) && (
                            <div className="text-xs text-gray-400 mt-0.5">
                                {p.skus?.length > 0 && <>SKU: {p.skus.join(", ")}</>}
                                {p.skus?.length > 0 && p.upcs?.length > 0 && " · "}
                                {p.upcs?.length > 0 && <>UPC: {p.upcs.join(", ")}</>}
                            </div>
                        )}
                        {p.lot_code && <div className="text-xs text-gray-400 mt-0.5">{t(`${T}.lot`)}: {p.lot_code}</div>}
                    </li>
                );
            })}
        </ul>
    );
};

const BoxGroup = ({ group, highlightProductId, loose, t }) => (
    <div className={`rounded-md p-2.5 ${loose ? "border-l-4 border-sky-400 border-y border-r border-gray-200 bg-white" : "bg-sky-50/60 border border-sky-100"}`}>
        <div className="flex items-center gap-2 flex-wrap">
            <Badge className="bg-sky-100 text-sky-800">{t(`${T}.box`)}</Badge>
            <span className="text-sm font-semibold text-gray-900">
                {qty(group.count, t(`${T}.box_one`), t(`${T}.box_other`))}
            </span>
            {loose && <span className="text-xs text-gray-400">{t(`${T}.loose`)}</span>}
            <span className="text-sm text-gray-500">
                · {t(`${T}.code`)}: <Barcode value={group.barcode} t={t} />
            </span>
        </div>
        <ProductLines products={group.products} highlightProductId={highlightProductId} t={t} />
    </div>
);

const Pallet = ({ container, count, highlightProductId, t }) => {
    const boxGroups = groupBoxes(container.children);
    return (
        <div className="rounded-lg border border-gray-200 border-l-4 border-l-amber-400 bg-white p-3 mb-3 shadow-sm">
            <div className="flex items-center gap-2 flex-wrap">
                <Badge className="bg-amber-100 text-amber-800">{t(`${T}.pallet`)}</Badge>
                <span className="text-sm font-semibold text-gray-900">
                    {qty(count, t(`${T}.pallet_one`), t(`${T}.pallet_other`))}
                </span>
                <span className="text-sm text-gray-500">
                    · {t(`${T}.code`)}: <Barcode value={container.barcode} t={t} />
                </span>
            </div>
            <ProductLines products={container.products} highlightProductId={highlightProductId} t={t} />
            {boxGroups.length > 0 && (
                <div className="mt-3 pl-3 ml-1 border-l-2 border-amber-200 space-y-2">
                    {boxGroups.map((group) => (
                        <BoxGroup key={group.key} group={group} highlightProductId={highlightProductId} t={t} />
                    ))}
                </div>
            )}
        </div>
    );
};

export const LocationContainersPanel = ({ open, onClose, warehouseId, location }) => {
    const { i18n } = useTranslation();
    const t = i18n.t.bind(i18n);
    const locationId = location?.location_id;

    const { data, isLoading, isError, error } = useQuery(
        ["location_containers", warehouseId, locationId],
        () => fetchLocationContainers(warehouseId, locationId),
        { enabled: !!open && !!locationId, refetchOnWindowFocus: false }
    );

    const containers = data?.containers || [];
    const palletGroups = groupPallets(containers.filter((c) => c.type === "PALLET"));
    const looseBoxGroups = groupBoxes(containers.filter((c) => c.type !== "PALLET"));

    return (
        <Drawer
            title={
                <div>
                    <div className="text-base font-semibold">{t(`${T}.title`)}</div>
                    <div className="text-sm text-gray-500 font-normal">
                        {location?.unique_code}
                        {location?.location_type ? ` · ${location.location_type}` : ""}
                    </div>
                </div>
            }
            placement="right"
            width={480}
            onClose={onClose}
            visible={open}
            bodyStyle={{ background: "#f9fafb" }}
        >
            {isLoading ? (
                <Loader show={true} />
            ) : isError ? (
                <div className="text-red-600 text-sm">Error: {error?.message}</div>
            ) : containers.length === 0 ? (
                <div className="text-gray-400 text-sm">{t(`${T}.empty`)}</div>
            ) : (
                <>
                    {palletGroups.map((g) => (
                        <Pallet key={g.key} container={g.pallet} count={g.count} highlightProductId={location?.product_id} t={t} />
                    ))}
                    {looseBoxGroups.length > 0 && (
                        <div className="space-y-2">
                            {looseBoxGroups.map((group) => (
                                <BoxGroup key={group.key} group={group} highlightProductId={location?.product_id} loose t={t} />
                            ))}
                        </div>
                    )}
                </>
            )}
        </Drawer>
    );
};
